import { ReviewStatisticsResponse } from '../infrastructure/review/utils/types';

/**
 * Format a date string into a relative format (Today, Yesterday, X days ago, etc.)
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
export function formatRelativeDate(dateString: string): string {
  if (!dateString) {
    return 'Recently';
  }

  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  } else {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
}

/**
 * Creates a mapping of product IDs to their review statistics for easier lookup
 * @param reviewStats - Review statistics response from the API
 * @returns A record object mapping product ID to average rating and total reviews count
 */
export function createReviewStatsMap(
  reviewStats?: ReviewStatisticsResponse,
): Record<number, { averageRate: number; totalReviews: number }> {
  if (!reviewStats?.statistics) {
    return {};
  }

  const map: Record<number, { averageRate: number; totalReviews: number }> = {};

  reviewStats.statistics.forEach((stat) => {
    map[stat.productId] = {
      averageRate: stat.averageRate,
      totalReviews: stat.totalReviews,
    };
  });

  return map;
}
/**
 * Calculates the fill width for a star in a rating display.
 *
 * @param rating - The rating value (0-5)
 * @param starIndex - The index of the star (0-based)
 * @returns The fill width of the star (percentage string)
 *
 * @example
 * getStarFillWidth(4.8, 4) // '80%' (5th star is 80% filled)
 * getStarFillWidth(3.0, 2) // '100%' (3rd star is completely filled)
 * getStarFillWidth(2.5, 3) // '0%' (4th star is not filled)
 */
export function getStarFillWidth(rating: number, starIndex: number): number {
  const starNumber = starIndex + 1;

  if (starNumber <= rating) {
    return 100;
  } else if (starNumber - 1 < rating) {
    const fillPercentage = (rating - (starNumber - 1)) * 100;
    return fillPercentage;
  }

  return 0;
}
